import React, { useRef, useState, useEffect } from 'react';
import './AetherisSplashAuth.css';

// 2026-08-22 owner request (website splash port, finished 2026-08-23): plays the SAME
// intro clip as the Android app (intro_splash.mp4, ~2MB/~10s, byte-identical to the
// app's res/raw copy) and mirrors SplashActivity.kt's behavior: play once on launch →
// 500ms fade → proceed; a 12s hard safety timeout so the splash can never block the
// site even if the video stalls or its events never fire; and any load/decode error
// skips straight to login with no fade (same as the app's onError → proceedToPairing).
export default function AetherisSplashAuth({ onComplete, muted = true }) {
  const videoRef = useRef(null);
  const [phase, setPhase] = useState('playing'); // playing | glitch | done
  const proceededRef = useRef(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // Safety net (mirrors SplashActivity's 12s handler.postDelayed): never let the
    // splash block the site for more than 12 seconds, even if onEnded/onError never fire.
    const safetyTimeout = setTimeout(handleEnd, 12000);

    // Attempt autoplay (muted required by browsers)
    v.play().catch(() => {
      // Autoplay blocked — skip straight to login
      handleEnd();
    });
    return () => clearTimeout(safetyTimeout);
  }, []);

  const handleEnd = () => {
    if (proceededRef.current) return;
    proceededRef.current = true;
    setPhase('glitch');
    setTimeout(() => {
      setPhase('done');
      onComplete();
    }, 500);
  };

  // Video failed to load/decode — proceed immediately, no fade (app parity:
  // SplashActivity's onError goes straight to LoginActivity with no animation).
  const handleError = () => {
    if (proceededRef.current) return;
    proceededRef.current = true;
    setPhase('done');
    onComplete();
  };

  return (
    <div className={`aetheris-splash${phase === 'done' ? ' fade-out' : ''}`}
      style={{ background: '#000', cursor: 'default' }}>

      {/* Full-viewport video */}
      <video
        ref={videoRef}
        src="/intro_splash.mp4"
        muted={muted}
        playsInline
        preload="auto"
        onEnded={handleEnd}
        onError={handleError}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 1,
        }}
      />

      {/* Scanlines always on top of video */}
      <div className="splash-scanlines" style={{ zIndex: 2 }} />
      <div className="splash-vignette" style={{ zIndex: 3 }} />

      {/* Glitch flash on transition */}
      {phase === 'glitch' && <div className="splash-glitch" style={{ zIndex: 10 }} />}
    </div>
  );
}
